/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper, UpIcon, DownIcon } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, formatToDatePeriod, getMaxTickLimit, getLabels, getSuggestedMaxTick, ETimePeriod } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { getBudgetProgressColor, RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 15,
                flexFlow: 'row wrap',
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0 10px 10px 10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
                '& .sub': {
                    display: 'flex',
                    flexFlow: 'row wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                '& .status': {
                    marginLeft: '10px',
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'center',
                },
            },
            '& .increase': {
                color: 'green',
            },
            '& .decrease': {
                color: 'red',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum LastCountEnum {
    PREV_MONTH,
    MONTH,
    FINANCIAL_YEAR,
    PREV_FINANCIAL_YEAR,
}

interface CumulativeBillableHoursVsBudgetChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface TimeTimeData {
    statistics: Statistics;
}

export interface Statistics {
    time: Time;
}

export interface Time {
    cumulativeBillableHoursVsBudget: TimeRecordedWithBudgetYearToDateAggregate;
}

export interface TimeRecordedWithBudgetYearToDateAggregate {
    lastMonth: BillableTimeGrouping;
    lastFinancialYear: BillableTimeGrouping;
    previousFinancialYear: BillableTimeGrouping;
    previousMonth: BilleTimeGroupingPreviousMonth;
}

export interface BillableTimeGrouping {
    summary: Summary;
    timesheetTimeWithBudgetGrouping: TimesheetTimeWithBudgetGrouping[];
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
    budget: number;
    variance: number;
    percentage: number;
}

export interface TimesheetTimeWithBudgetGrouping {
    billed: number;
    unbilled: number;
    budget: number;
    group: string;
    valueAmount: number;
}

export interface BilleTimeGroupingPreviousMonth {
    summary: Summary;
    financialYearToPreviousMonthSummary: Summary;
    timesheetTimeWithBudgetGrouping: TimesheetTimeWithBudgetGrouping[];
}

interface ChartState {
    last: LastCountEnum;
    months: string[];
    billed: number[];
    unbilled: number[];
    budgets: number[];
    valueAmounts: number[];
    currentYearTotal: number;
    currentTotal: number;
    leftPeriodString: string;
    budgetTotal: number;
    variance: number;
    budgetPercentage: number;
    yearBudget: number;
    yearVariance: number;
    yearPercentage: number;
    rightPeriodString: string;
    symbol: string;
}

export const CumulativeBillableHoursVsBudgetChart: React.FC<CumulativeBillableHoursVsBudgetChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const [state, setState] = useState<ChartState>({
        last: LastCountEnum.MONTH,
        months: [],
        billed: [],
        unbilled: [],
        budgets: [],
        valueAmounts: [],
        currentYearTotal: 0,
        currentTotal: 0,
        leftPeriodString: formatToDatePeriod('monthToDate'),
        yearBudget: 0,
        yearVariance: 0,
        yearPercentage: 0,
        budgetTotal: 0,
        budgetPercentage: 0,
        variance: 0,
        rightPeriodString: formatToDatePeriod('yearToDate'),
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<TimeTimeData>(ChartData, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let months: string[] = [];
                let billed: number[] = [];
                let unbilled: number[] = [];
                let budgets: number[] = [];
                let valueAmounts: number[] = [];
                let currentTotal: number = 0;
                let currentYearTotal: number = 0;
                let leftPeriodString: string = '';
                let yearBudget: number = 0;
                let yearVariance: number = 0;
                let yearPercentage: number = 0;
                let budgetTotal: number = 0;
                let budgetPercentage: number = 0;
                let variance: number = 0;
                let rightPeriodString: string = formatToDatePeriod('yearToDate');

                if (!error && data) {
                    var lastMonth = data.statistics.time.cumulativeBillableHoursVsBudget.lastMonth;
                    var lastYear = data.statistics.time.cumulativeBillableHoursVsBudget.lastFinancialYear;
                    var prevYear = data.statistics.time.cumulativeBillableHoursVsBudget.previousFinancialYear;
                    var prevMonth = data.statistics.time.cumulativeBillableHoursVsBudget.previousMonth;

                    yearBudget = lastYear.summary.budget;
                    yearVariance = lastYear.summary.variance;
                    yearPercentage = lastYear.summary.percentage;

                    currentYearTotal = lastYear.summary.lastPeriodTotal;

                    if (state.last === LastCountEnum.MONTH) {
                        if (lastMonth.timesheetTimeWithBudgetGrouping.length > 0) {
                            months = lastMonth.timesheetTimeWithBudgetGrouping.map((s) => s.group);
                            billed = lastMonth.timesheetTimeWithBudgetGrouping.map((s) => s.billed);
                            unbilled = lastMonth.timesheetTimeWithBudgetGrouping.map((s) => s.unbilled);
                            budgets = lastMonth.timesheetTimeWithBudgetGrouping.map((s) => s.budget);
                            valueAmounts = lastMonth.timesheetTimeWithBudgetGrouping.map((s) => s.valueAmount);
                        }

                        budgetTotal = lastMonth.summary.budget;
                        variance = lastMonth.summary.variance;
                        budgetPercentage = lastMonth.summary.percentage;

                        currentTotal = lastMonth.summary.lastPeriodTotal;

                        leftPeriodString = formatToDatePeriod('monthToDate');
                    } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                        if (lastYear.timesheetTimeWithBudgetGrouping.length > 0) {
                            months = lastYear.timesheetTimeWithBudgetGrouping.map((s) => s.group);
                            billed = lastYear.timesheetTimeWithBudgetGrouping.map((s) => s.billed);
                            unbilled = lastYear.timesheetTimeWithBudgetGrouping.map((s) => s.unbilled);
                            budgets = lastYear.timesheetTimeWithBudgetGrouping.map((s) => s.budget);
                            valueAmounts = lastYear.timesheetTimeWithBudgetGrouping.map((s) => s.valueAmount);
                        }

                        budgetTotal = lastYear.summary.budget;
                        variance = lastYear.summary.variance;
                        budgetPercentage = lastYear.summary.percentage;

                        currentTotal = lastYear.summary.lastPeriodTotal;

                        leftPeriodString = formatToDatePeriod('yearToDate');
                    } else if (state.last === LastCountEnum.PREV_MONTH) {
                        if (prevMonth.timesheetTimeWithBudgetGrouping.length > 0) {
                            months = prevMonth.timesheetTimeWithBudgetGrouping.map((s) => s.group);
                            billed = prevMonth.timesheetTimeWithBudgetGrouping.map((s) => s.billed);
                            unbilled = prevMonth.timesheetTimeWithBudgetGrouping.map((s) => s.unbilled);
                            budgets = prevMonth.timesheetTimeWithBudgetGrouping.map((s) => s.budget);
                            valueAmounts = prevMonth.timesheetTimeWithBudgetGrouping.map((s) => s.valueAmount);
                        }

                        budgetTotal = prevMonth.summary.budget;
                        variance = prevMonth.summary.variance;
                        budgetPercentage = prevMonth.summary.percentage;

                        currentTotal = prevMonth.summary.lastPeriodTotal;

                        if (prevMonth.financialYearToPreviousMonthSummary) {
                            yearBudget = prevMonth.financialYearToPreviousMonthSummary.budget;
                            yearVariance = prevMonth.financialYearToPreviousMonthSummary.variance;
                            yearPercentage = prevMonth.financialYearToPreviousMonthSummary.percentage;
                            currentYearTotal = prevMonth.financialYearToPreviousMonthSummary.lastPeriodTotal;
                        } else {
                            yearBudget = 0;
                            yearVariance = 0;
                            yearPercentage = 0;
                            currentYearTotal = 0;
                        }

                        leftPeriodString = formatToDatePeriod('previousMonth');
                        rightPeriodString = formatToDatePeriod('previousMonthYearToDate');
                    } else {
                        if (prevYear.timesheetTimeWithBudgetGrouping.length > 0) {
                            months = prevYear.timesheetTimeWithBudgetGrouping.map((s) => s.group);
                            billed = prevYear.timesheetTimeWithBudgetGrouping.map((s) => s.billed);
                            unbilled = prevYear.timesheetTimeWithBudgetGrouping.map((s) => s.unbilled);
                            budgets = prevYear.timesheetTimeWithBudgetGrouping.map((s) => s.budget);
                            valueAmounts = prevYear.timesheetTimeWithBudgetGrouping.map((s) => s.valueAmount);
                        }

                        budgetTotal = prevYear.summary.budget;
                        variance = prevYear.summary.variance;
                        budgetPercentage = prevYear.summary.percentage;

                        currentTotal = prevYear.summary.lastPeriodTotal;

                        leftPeriodString = formatToDatePeriod('previousYearToDate');
                    }
                }

                return {
                    ...prevState,
                    months,
                    billed,
                    unbilled,
                    budgets,
                    valueAmounts,
                    currentTotal,
                    currentYearTotal,
                    leftPeriodString,
                    yearBudget,
                    yearPercentage,
                    yearVariance,
                    budgetTotal,
                    variance,
                    budgetPercentage,
                    rightPeriodString,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading, data, error]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isCumulativeBillableHoursVsBudgetChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isFinancialYear: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let timePeriod = ETimePeriod.THIS_MONTH;
            if (!isFinancialYear) {
                if (state.last === LastCountEnum.MONTH) {
                    searchCriterias.push({ property: 'MonthToDate', operator: '', comparison: 'curr' });
                    timePeriod = ETimePeriod.THIS_MONTH;
                } else if (state.last === LastCountEnum.PREV_MONTH) {
                    searchCriterias.push({ property: 'MonthToDate', operator: '', comparison: 'prev' });
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: 'curr' });
                    timePeriod = ETimePeriod.FINANCIAL_YEAR;
                } else if (state.last === LastCountEnum.PREV_FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: 'prev' });
                    timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                }
            } else {
                var period = 'curr';
                timePeriod = ETimePeriod.FINANCIAL_YEAR;
                var comparison = 'curr';
                if (state.last === LastCountEnum.PREV_MONTH) {
                    comparison = 'prevMonth';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                }
                
                searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: comparison, period: period });
            }
            
            searchCriterias.push({ property: 'Amount', operator: 'gt', comparison: '0' });

            props.getSelectedItem(
                searchCriterias, 
                false, 
                `Drilldown from Cumulative Billable Hours Recorded vs Budget for ${timePeriod}`, 
                DrillDownType.timesheetSummary, 
                'Timesheet Lines'
            );
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.budgets, state.billed, state.unbilled) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<AccessTimeOutlinedIcon />} title="Cumulative Billable Hours Recorded vs Budget" />
                <div className="colWrapper">
                    <div className="col">
                        <div className="title">{state.leftPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentTotal} format={(amount) => formatNumber(amount)} /> hrs
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.budgetPercentage))}%`, backgroundColor: getBudgetProgressColor(state.budgetPercentage) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(false)}>
                            {`Budget ${formatNumber(state.budgetTotal)} hrs`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(false)}>
                                {`${formatNumber(state.budgetPercentage)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetTotal < state.currentTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(false)} title="Variance">
                                {state.budgetTotal < state.currentTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${formatNumber(state.variance)} hrs`}
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">{state.rightPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentYearTotal} format={(amount) => formatNumber(amount)} /> hrs
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.yearPercentage))}%`, backgroundColor: getBudgetProgressColor(state.yearPercentage) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(true)}>
                            {`Budget ${formatNumber(state.yearBudget)} hrs`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(true)}>
                                {`${formatNumber(state.yearPercentage)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.yearBudget < state.currentYearTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(true)} title="Variance">
                                {state.yearBudget < state.currentYearTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${formatNumber(state.yearVariance)} hrs`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {

                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.months[_index];

                            const searchCriterias = new Array<SearchCriteria>();

                            let timePeriod = ETimePeriod.THIS_MONTH;

                            if (state.last === LastCountEnum.MONTH) {
                                searchCriterias.push({ property: 'MonthToDate', operator: '', comparison: 'curr' }, { property: 'Amount', operator: 'gt', comparison: '0' });
                                timePeriod = ETimePeriod.THIS_MONTH;
                            } else if (state.last === LastCountEnum.PREV_MONTH) {
                                searchCriterias.push({ property: 'MonthToDate', operator: '', comparison: 'prev' }, { property: 'Amount', operator: 'gt', comparison: '0' });
                                timePeriod = ETimePeriod.PREVIOUS_MONTH;
                            } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                                searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: 'curr' }, { property: 'Amount', operator: 'gt', comparison: '0' });
                                timePeriod = ETimePeriod.FINANCIAL_YEAR;
                            } else if (state.last === LastCountEnum.PREV_FINANCIAL_YEAR) {
                                searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: 'prev' }, { property: 'Amount', operator: 'gt', comparison: '0' });
                                timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                            }

                            const title = `Drilldown from Cumulative Billable Hours Recorded vs Budget for ${timePeriod} - ${item}`;

                            props.getSelectedItem(
                                searchCriterias, 
                                false, 
                                title,
                                DrillDownType.timesheetSummary, 
                                'Timesheet Lines'
                            );
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Budget',
                                backgroundColor: RV_CHART_COLORS.amber,
                                borderColor: RV_CHART_COLORS.amber,
                                borderWidth: 3,
                                hoverBackgroundColor: RV_CHART_COLORS.amber,
                                hoverBorderColor: RV_CHART_COLORS.amber,
                                tension: 0.35,
                                pointRadius: 0,
                                data: state.budgets,
                                type: 'line',
                                pointStyle: 'line',
                            },
                            {
                                fill: false,
                                label: 'Billable',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                data: state.billed,
                                type: 'bar',
                            },
                            {
                                fill: false,
                                label: 'Non-Billable',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft, true),
                                data: state.unbilled,
                                type: 'bar',
                            },
                            // tslint:disable-next-line: no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                stacked: true,
                                suggestedMax: getSuggestedMaxTick([...state.billed, ...state.unbilled, ...state.budgets]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.billed, ...state.unbilled, ...state.budgets]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)} hrs`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                stacked: true,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                                // min: 0,
                                // max: 9 // minimum display
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${formatNumber(currentValue, 2)} hrs`;
                                    },
                                    // tslint:disable-next-line: no-any no-shadowed-variable
                                    afterLabel: function (context: any) {
                                        const {
                                            dataIndex,
                                            //   label,
                                            // formattedValue,
                                            dataset,
                                        } = context;

                                        const feeAmount = state.valueAmounts[dataIndex];
                                        const _billed = state.billed[dataIndex];

                                        if (dataset.label.toLowerCase() !== 'billable' || _billed === 0) {
                                            return [];
                                        }
                                        return [`Fee Amount: ${state.symbol}${formatNumber(feeAmount, 2)}`];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.budgets, state.billed, state.unbilled) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={LastCountEnum.PREV_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={LastCountEnum.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={LastCountEnum.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

const ChartData = gql`
fragment LastTimesheetTimeWithBudgetInPeriod on TimesheetTimeWithBudgetInPeriod {
    summary {
        lastPeriodTotal
        previousPeriodTotal
        budget
        variance
        percentage
    }
    timesheetTimeWithBudgetGrouping {
        billed
        unbilled
        budget
        group
        valueAmount
        __typename
    }
}

query cummulativeHoursRecordedVsBudget($entityType: Int, $entityGuid: String, $responsibility: Int) {
    statistics {
        time {
            cumulativeBillableHoursVsBudget(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    lastMonth {
                        ...LastTimesheetTimeWithBudgetInPeriod
                    }
                    lastFinancialYear {
                    ...LastTimesheetTimeWithBudgetInPeriod
                    }
                    previousFinancialYear {
                    ...LastTimesheetTimeWithBudgetInPeriod
                    }
                    previousMonth {
                    ...LastTimesheetTimeWithBudgetInPeriod
                    financialYearToPreviousMonthSummary {
                        lastPeriodTotal
                        previousPeriodTotal
                        budget
                        variance
                        percentage
                    }
                }
            }
        }
    }
}
`;
